import { getJerseyPatternId } from '@sportninja/common/features/game/attendance/utils/jerseyPattern'
import {
  getGenericSportById,
  getSportById,
} from '@sportninja/common/selectors/types'
import { useSelector } from 'src/hooks/useRedux'
import useGameJerseyColors from 'src/pages/Game/hooks/useGameJerseyColors'
import type { AttendanceGameRecord } from './useAttendanceGameMeta'

interface JerseySport {
  jersey_type_id?: string | number | null
}

export interface AttendanceJerseys {
  /** JerseyPattern key (1 soccer / 2 hockey / 3 basketball). */
  pattern?: string | number
  homeColor: string
  visitorColor: string
}

/**
 * Each team's jersey for the game card: the sport's jersey pattern and the
 * team's game jersey color — the same colors the game page uses
 * (useGameJerseyColors), the same pattern rule as mobile (getJerseyPatternId).
 */
export const useAttendanceJerseys = (
  gameId: string,
  game: AttendanceGameRecord | undefined
): AttendanceJerseys => {
  'use memo'
  // The game loads after mount, so its ids are listed as deps — otherwise the
  // lookup keeps its first (empty) result and the jersey falls back to hockey.
  const sport = useSelector<JerseySport | null>(
    (state) => (getSportById(state, game?.sport_id) as JerseySport) || null,
    [game?.sport_id]
  )
  const genericSport = useSelector<JerseySport | null>(
    (state) =>
      (getGenericSportById(state, game?.generic_sport_id) as JerseySport) ||
      null,
    [game?.generic_sport_id]
  )
  const { homeJerseyColor, visitorJerseyColor } = useGameJerseyColors({
    gameId,
    game: {
      homeTeamId: game?.homeTeam?.id ?? undefined,
      visitingTeamId: game?.visitingTeam?.id ?? undefined,
    },
  })

  return {
    pattern: getJerseyPatternId(game, sport, genericSport),
    homeColor: homeJerseyColor,
    visitorColor: visitorJerseyColor,
  }
}
